import { FC, useEffect } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { TrackedDialog } from '@eneco/components/TrackedDialog/TrackedDialog';
import { Header, Layout, NextButton } from '@eneco/flows/components';
import { useFlowHooks } from '@eneco/flows/utils/FlowProvider';
import { typedYupResolver } from '@eneco/hooks/form';
import { useFormatter } from '@eneco/i18n';
import { Grid, NotificationBox, RadioGroup, RadioTile, Stack, Stretch, Text, TextLink } from '@eneco/sparky';
import { usePlaceholderContent } from '@sitecore/common';
import type { AddressDetailsStepRendering } from '@sitecore/types/AddressDetailsStep';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { FlowContextSme } from '../../context';

interface FormFields {
  hasResidencePurpose: string;
}

export const AddressDetailsStep: FC = () => {
  const { useFlowActorRef, useFlowSelector } = useFlowHooks<FlowContextSme>();

  const { send: sendToFlowMachine } = useFlowActorRef();
  const { address } = useFormatter();
  const hasResidencePurpose = useFlowSelector(state => state.context.hasResidencePurpose);
  const city = useFlowSelector(state => state.context.city);
  const postalCode = useFlowSelector(state => state.context.postalCode);
  const houseNumber = useFlowSelector(state => state.context.houseNumber);
  const houseNumberSuffix = useFlowSelector(state => state.context.houseNumberSuffix);
  const street = useFlowSelector(state => state.context.street);

  const { AddressDetailsStepSME: { fields: textualData } = {} } = usePlaceholderContent<{
    AddressDetailsStepSME: AddressDetailsStepRendering;
  }>();

  const formSchema = yup.object({
    hasResidencePurpose: yup.string().required(textualData?.choice?.hasResidencePurposeRadio?.value?.requiredMessage),
  });

  const {
    handleSubmit,
    control,
    getValues,
    formState: { errors, isSubmitSuccessful },
  } = useForm<FormFields>({
    mode: 'onSubmit',
    defaultValues: {
      hasResidencePurpose: hasResidencePurpose?.toString(),
    },
    resolver: typedYupResolver<FormFields>(formSchema),
  });

  useEffect(() => {
    if (isSubmitSuccessful) {
      const formData = getValues();

      sendToFlowMachine({
        type: 'NEXT_PAGE',
        values: {
          hasResidencePurpose: formData.hasResidencePurpose === 'true',
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitSuccessful]);

  return (
    <Layout.Content variant="A" handleSubmit={handleSubmit}>
      <Header>
        <Header.Title>{textualData?.content?.title?.value}</Header.Title>
        {textualData?.content?.text?.value && (
          <Header.Text>
            {textualData.content.text.value}{' '}
            <TrackedDialog
              title={textualData?.info?.residenceFunctionDialog?.value?.title}
              trigger={<TextLink>{textualData?.info?.residenceFunctionDialog?.value?.triggerText}</TextLink>}>
              <RichText html={textualData?.info?.residenceFunctionDialog?.value?.content} />
            </TrackedDialog>
          </Header.Text>
        )}
      </Header>

      {textualData?.content?.notification?.value?.content && (
        <NotificationBox
          isAlert={false}
          variant={textualData?.content?.notification?.value?.variant}
          title={textualData?.content?.notification?.value?.title}
          text={<RichText html={textualData?.content?.notification?.value?.content} />}
        />
      )}

      <Stack.Item>
        <Text size="BodyS" weight="bold">
          {textualData?.content?.addressText?.value}
        </Text>
        <Text>{address.long({ street, houseNumber, houseNumberSuffix, postalCode, city })}</Text>
      </Stack.Item>

      <Controller
        control={control}
        name="hasResidencePurpose"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            name={name}
            label={textualData?.choice?.hasResidencePurposeRadio?.value?.label ?? ''}
            value={value}
            onValueChange={onChange}
            error={errors['hasResidencePurpose']?.message}>
            <Stretch>
              <Grid gridTemplateColumns="1fr 1fr" gap="4">
                {textualData?.choice?.hasResidencePurposeRadio?.value?.options?.map(option => (
                  <RadioTile value={option.name} key={option.name}>
                    {option.label}
                  </RadioTile>
                ))}
              </Grid>
            </Stretch>
          </RadioGroup>
        )}
      />

      <NextButton isLoading={isSubmitSuccessful}>{textualData?.content?.nextStepText?.value}</NextButton>
    </Layout.Content>
  );
};
